import React from 'react';
import {
  Modal,
  Stack,
  TextContainer,
  Heading,
  List,
  TextStyle
} from '@shopify/polaris';

/**
 * A reusable component for displaying template and workflow help information
 * 
 * @param {Object} props
 * @param {boolean} props.open - Whether the modal is open
 * @param {Function} props.onClose - Callback when modal is closed
 */
export const TemplateHelp = ({
  open,
  onClose
}) => {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Template & Workflow Help"
      secondaryActions={[
        {
          content: 'Close',
          onAction: onClose
        }
      ]}
    >
      <Modal.Section>
        <Stack vertical spacing="loose">
          <TextContainer>
            <Heading>Templates</Heading>
            <p>Templates are AI-powered tools that help you modify product information in bulk. Each template consists of:</p>
            <ul>
              <li><TextStyle variation="strong">Input Fields:</TextStyle> The product information that will be used to generate new content</li>
              <li><TextStyle variation="strong">Output Fields:</TextStyle> The fields that will be updated with the generated content</li>
              <li><TextStyle variation="strong">Prompt:</TextStyle> Instructions for the AI on how to transform the input into output</li>
            </ul>
          </TextContainer>

          <TextContainer>
            <Heading>Template Best Practices</Heading>
            <List type="bullet">
              <List.Item>
                <TextStyle variation="strong">Writing Prompts:</TextStyle>
                <ul>
                  <li>Be specific about the desired output format and length</li>
                  <li>Use placeholders like {'{'}field_name{'}'} to reference input fields</li>
                  <li>Include any brand voice or style guidelines</li>
                  <li>Specify if HTML formatting should be included</li>
                </ul>
              </List.Item>
              <List.Item>
                <TextStyle variation="strong">Field Selection:</TextStyle>
                <ul>
                  <li>Only select input fields that are relevant to your prompt</li>
                  <li>Choose output fields that match your intended updates</li>
                  <li>Use metafields for custom data storage</li>
                </ul>
              </List.Item>
            </List>
          </TextContainer>

          <TextContainer>
            <Heading>Workflows</Heading>
            <p>Workflows allow you to combine multiple templates and run them in sequence. This is useful when you want to:</p>
            <ul>
              <li>Generate multiple types of content at once (e.g., title, description, and SEO fields)</li>
              <li>Apply templates in a specific order</li>
              <li>Save commonly used template combinations</li>
            </ul>
          </TextContainer>

          <TextContainer>
            <Heading>Workflow Best Practices</Heading>
            <List type="bullet">
              <List.Item>
                <TextStyle variation="strong">Template Order:</TextStyle>
                <ul>
                  <li>Order templates from general to specific</li>
                  <li>Group related templates together</li>
                  <li>Consider dependencies between fields</li>
                </ul>
              </List.Item>
              <List.Item>
                <TextStyle variation="strong">Testing:</TextStyle>
                <ul>
                  <li>Test workflows on a small batch of products first</li>
                  <li>Review the changes before pushing to Shopify</li>
                  <li>Use the undo feature if needed</li>
                </ul>
              </List.Item>
            </List>
          </TextContainer>
        </Stack>
      </Modal.Section>
    </Modal>
  );
};

export default TemplateHelp;
